import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { 
  Lock, Clock, Users, Save, Loader2, Info, Shield, AlertTriangle,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useUser } from '@/utils/userHelper';

export default function LockSettings({ user: propUser, isTab, tabId }) {
  const user = useUser(propUser);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeLocks, setActiveLocks] = useState(0);
  const [settings, setSettings] = useState({
    lock_timeout_minutes: 15,
    enable_locking: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsRes, countRes] = await Promise.all([
        api.get('/locks/settings'),
        api.get('/locks/active-count')
      ]);
      setSettings(settingsRes.data);
      setActiveLocks(countRes.data.active_locks || 0);
    } catch (error) {
      console.error('Failed to load lock settings:', error);
      toast.error('Failed to load lock settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (settings.lock_timeout_minutes < 1 || settings.lock_timeout_minutes > 60) {
      toast.error('Lock timeout must be between 1 and 60 minutes');
      return;
    }

    setSaving(true);
    try {
      await api.put('/locks/settings', settings);
      toast.success('Lock settings saved successfully');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const handleCleanup = async () => {
    try {
      const res = await api.delete('/locks/cleanup');
      toast.success(`Cleaned up ${res.data.deleted} expired locks`);
      loadData();
    } catch (error) {
      toast.error('Failed to cleanup locks');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const canEdit = ['super_admin', 'hotel_admin', 'company_admin'].includes(user?.role);

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Lock className="h-6 w-6 text-orange-600" />
            Resource Lock Settings
          </h1>
          <p className="text-gray-500 mt-1">
            Configure how resource locking works for your hotel
          </p>
        </div>
        <Badge className="bg-blue-100 text-blue-700 px-3 py-1">
          <Users className="h-4 w-4 mr-1" />
          {activeLocks} Active Lock{activeLocks !== 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800 font-medium">What is Resource Locking?</p>
              <p className="text-sm text-blue-700 mt-1">
                When a user opens a Check-in or Checkout tab, the booking is locked to prevent 
                other users from accessing it simultaneously. This prevents conflicts when multiple 
                front desk staff work on the same booking.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-gray-600" />
            Lock Configuration
          </CardTitle>
          <CardDescription>
            Configure lock behavior and timeout settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable Locking */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <Label className="text-base font-medium">Enable Resource Locking</Label>
              <p className="text-sm text-gray-500 mt-1">
                When disabled, multiple users can access the same booking without locks
              </p>
            </div>
            <Switch
              checked={settings.enable_locking}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enable_locking: checked }))}
              disabled={!canEdit}
            />
          </div>

          {/* Lock Timeout */}
          <div className={`space-y-3 ${!settings.enable_locking ? 'opacity-50' : ''}`}>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-600" />
              <Label className="text-base font-medium">Lock Timeout</Label>
            </div>
            <p className="text-sm text-gray-500">
              How long before an inactive lock automatically expires (in minutes)
            </p>
            <div className="flex items-center gap-4">
              <Input
                type="number"
                min={1}
                max={60}
                value={settings.lock_timeout_minutes}
                onChange={(e) => setSettings(prev => ({ 
                  ...prev, 
                  lock_timeout_minutes: parseInt(e.target.value) || 15 
                }))}
                className="w-24"
                disabled={!canEdit || !settings.enable_locking}
              />
              <span className="text-gray-500">minutes</span>
              <div className="flex gap-2">
                {[5, 10, 15, 30].map(mins => (
                  <Button
                    key={mins}
                    type="button"
                    variant={settings.lock_timeout_minutes === mins ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSettings(prev => ({ ...prev, lock_timeout_minutes: mins }))}
                    disabled={!canEdit || !settings.enable_locking}
                    className={settings.lock_timeout_minutes === mins ? 'bg-orange-500 hover:bg-orange-600' : ''}
                  >
                    {mins}m
                  </Button>
                ))}
              </div>
            </div>
            {settings.lock_timeout_minutes < 5 && (
              <div className="flex items-center gap-2 text-amber-600 text-sm">
                <AlertTriangle className="h-4 w-4" />
                Short timeout may cause frequent lock expirations during normal operations
              </div>
            )}
          </div>

          {/* Save Button */}
          {canEdit && (
            <div className="flex gap-3 pt-4 border-t">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-orange-500 hover:bg-orange-600"
              >
                {saving ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving...</>
                ) : (
                  <><Save className="h-4 w-4 mr-2" /> Save Settings</>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleCleanup}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Cleanup Expired Locks
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* How Locking Works */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">How Resource Locking Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-emerald-50 rounded-lg">
              <h4 className="font-medium text-emerald-800 mb-2">When Lock is Acquired</h4>
              <ul className="text-sm text-emerald-700 space-y-1">
                <li>• User opens Check-in or Checkout tab</li>
                <li>• Booking is locked for that user</li>
                <li>• Other users see "In use by [name]"</li>
                <li>• Lock auto-renews while tab is open</li>
              </ul>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-medium text-purple-800 mb-2">When Lock is Released</h4>
              <ul className="text-sm text-purple-700 space-y-1">
                <li>• User closes the tab</li>
                <li>• Operation completes (check-in/checkout)</li>
                <li>• Timeout expires (if user abandons tab)</li>
                <li>• Admin manually cleans up expired locks</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
